import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { NzCardModule } from 'ng-zorro-antd/card';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';

import { AuthStore } from '@rapider/authentication';
import { PersonProjectControllerService, ProjectControllerService } from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import {
  WORKSPACE_CONFIG,
  buildDynamicProjectsFilter,
  getDeterministicCardImage,
  buildFavoriteProjectsFilter,
  extractFavoriteIds
} from '../workspace.config';

/**
 * @class WorkspaceSelectComponent
 * @description Renders the workspace selection portal. Supports pagination, searching, sorting, and filtering.
 */
@Component({
  selector: 'rapider-workspace-select',
  standalone: true,
  imports: [CommonModule, FormsModule, NzCardModule, NzTooltipModule, NzInputModule, NzSelectModule],
  templateUrl: './workspace-select.component.html',
  styleUrls: ['./workspace-select.component.scss'],
  host: {
    class: 'block h-full w-full overflow-hidden flex flex-col min-h-0 bg-body-background'
  }
})
export class WorkspaceSelectComponent implements OnInit, OnDestroy {
  private readonly authStore = inject(AuthStore);
  private readonly projectController = inject(ProjectControllerService);
  private readonly personProjectController = inject(PersonProjectControllerService);
  private readonly notificationService = inject(NotificationService);

  public readonly config = WORKSPACE_CONFIG;

  // Data State
  public localProjects = signal<any[]>([]);
  public favoriteIds = signal<Set<string>>(new Set());

  // UI State
  public isLoading = signal<boolean>(true);
  public isPaginating = signal<boolean>(false);
  public hasMoreProjects = signal<boolean>(false);

  // Toolbar State
  public searchQuery = signal<string>('');
  public sortValue = signal<string>('dateDesc');
  public filterValue = signal<string>('all');

  private readonly pageSize = 10;
  private searchSubject = new Subject<string>();
  private searchSubscription!: Subscription;

  constructor() {
    // Set up search debouncing to prevent spamming the API on every keystroke
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this.resetAndFetchProjects();
    });
  }

  ngOnInit(): void {
    // Initialize by fetching favorite ids to map UI stars, then load standard projects.
    this.loadFavoritesAndProjects();
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  /**
   * Fetches favorited projects on page load to populate the favoriteIds signal.
   * This is required so stars appear correctly on standard lists.
   */
  private loadFavoritesAndProjects(): void {
    this.isLoading.set(true);
    const filter = buildFavoriteProjectsFilter(this.pageSize, 0); // Increase limit if expecting many favorites

    // find projects favorited by the user to populate the favoriteIds set, this allows us to show the star icons as filled for favorited projects in the main list without needing to call findById for each project just to check if it's favorited
    this.personProjectController.find({ filter }).subscribe({
      next: (favorites: any[]) => {
        const ids = extractFavoriteIds(favorites);
        this.favoriteIds.set(new Set(ids));
        this.fetchProjects(true);
      },
      error: () => {
        // Even if fetching favorites fail, proceed to load main project list
        this.fetchProjects(true);
      }
    });
  }

  /**
   * Triggers a fresh fetch of projects, resetting pagination state.
   */
  public resetAndFetchProjects(): void {
    this.fetchProjects(true);
  }

  /**
   * Fetches projects from the API. Branches off to favorited filtering logic when needed.
   * @param {boolean} reset - If true, replaces existing list. If false, appends to the list (Load More).
   */
  public fetchProjects(reset: boolean = false): void {
    if (reset) {
      this.isLoading.set(true);
      this.localProjects.set([]);
    } else {
      this.isPaginating.set(true);
    }

    // Branch logic: if filtered by favorites, query projects using LoopBack `inq` filter on favorite ids
    if (this.filterValue() === 'favorited') {
      this.fetchFavoritedProjectsFullData();
      return;
    }

    const currentSkip = reset ? 0 : this.localProjects().length;
    // Note: Assuming authStore provides a way to get user ID, e.g., authStore.user()?.id
    const currentUserId = null; // Replace with actual user ID if tracking ownership

    const filter = buildDynamicProjectsFilter(
      this.pageSize,
      currentSkip,
      this.searchQuery(),
      this.sortValue(),
      this.filterValue(),
      this.favoriteIds(),
      currentUserId
    );

    this.projectController.find({ filter }).subscribe({
      next: (res) => {
        if (reset) {
          this.localProjects.set(res);
        } else {
          this.localProjects.update(prev => [...prev, ...res]);
        }

        // If the number of returned records matches the page size, there are likely more.
        this.hasMoreProjects.set(res.length === this.pageSize);

        this.isLoading.set(false);
        this.isPaginating.set(false);
      },
      error: () => {
        this.notificationService.createNotification('error', 'Error', this.config.notifications.fetchFailed);
        this.isLoading.set(false);
        this.isPaginating.set(false);
      }
    });
  }

  /**
   * Fetches full project data for favorited workspaces using a single `find` query with an `inq` id filter.
   * Favorites are capped at 10; no Load More pagination is needed for this view.
   * The API handles ordering; search is pushed to the `where` clause when active.
   */
  private fetchFavoritedProjectsFullData(): void {
    const ids = Array.from(this.favoriteIds());

    if (ids.length === 0) {
      this.localProjects.set([]);
      this.isLoading.set(false);
      this.isPaginating.set(false);
      this.hasMoreProjects.set(false);
      return;
    }

    // Resolve server-side sort order from the current sortValue signal
    const sortOrderMap: Record<string, string> = {
      alphabetical: 'name ASC',
      dateAsc: 'createdDate ASC',
      dateDesc: 'createdDate DESC'
    };
    const order = sortOrderMap[this.sortValue()] ?? 'createdDate DESC';

    // Build where clause: always filter by favorite ids; optionally add name search
    const where: Record<string, unknown> = {
      id: { inq: ids }
    };
    const q = this.searchQuery().trim();
    if (q) {
      where['name'] = { like: `.*${q}.*`, options: 'i' };
    }

    const filter = JSON.stringify({
      where,
      fields: { id: true, name: true, createdDate: true, createdBy: true, createdById: true },
      order: [order],
      limit: 10
    });

    this.projectController.find({ filter }).subscribe({
      next: (results) => {
        // Favorites are always a full (non-paginated) replace; no Load More for this view
        this.localProjects.set(results as []);
        this.hasMoreProjects.set(false);
        this.isLoading.set(false);
        this.isPaginating.set(false);
      },
      error: () => {
        this.notificationService.createNotification('error', 'Error', this.config.notifications.fetchFailed);
        this.isLoading.set(false);
        this.isPaginating.set(false);
      }
    });
  }

  /**
   * Emits the search string to the subject for debouncing.
   * @param {string} value - The input search string.
   */
  public onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.searchSubject.next(value);
  }

  public getCardImage(id: string): string {
    return getDeterministicCardImage(id);
  }

  public onSelectWorkspace(project: any): void {
    this.authStore.setActiveWorkspace({ project });
  }

  public toggleFavorite(event: Event, project: any): void {
    event.stopPropagation();

    const isFav = this.favoriteIds().has(project.id);
    const newFavorites = new Set(this.favoriteIds());

    if (isFav) {
      newFavorites.delete(project.id);
      this.favoriteIds.set(newFavorites);
      this.personProjectController.unfavorite({ projectId: project.id }).subscribe();
    } else {
      newFavorites.add(project.id);
      this.favoriteIds.set(newFavorites);
      this.personProjectController.favorite({ projectId: project.id }).subscribe();
    }

    // Auto-refresh the favorited view if they are currently on it and they unfavorited an item
    if (this.filterValue() === 'favorited' && isFav) {
      this.resetAndFetchProjects();
    }
  }
}